import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, Calendar, ExternalLink, X, AlertTriangle } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
} from "@dnd-kit/core";

import { api, formatApiError } from "@/lib/api";
import { isoLocal, parseIsoLocal } from "@/lib/dateUtils";
import PageHeader from "@/components/PageHeader";
import JobStatusBadge, { JobPriorityBadge } from "@/components/JobStatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const SLOT_TYPES = ["am", "pm", "all_day", "custom"];
const SLOT_LABEL = { am: "AM", pm: "PM", all_day: "All Day", custom: "Custom" };
const STATUS_BORDER = {
  new: "border-l-gray-400",
  scheduled: "border-l-blue-500",
  in_progress: "border-l-amber-500",
  completed: "border-l-indigo-500",
  closed: "border-l-green-500",
};

function iso(d) { return isoLocal(d); }
function mondayOf(d) {
  const day = d.getDay();
  const diff = (day + 6) % 7; // Monday=0
  const m = new Date(d);
  m.setDate(d.getDate() - diff);
  m.setHours(0, 0, 0, 0);
  return m;
}
function addDays(d, n) { const c = new Date(d); c.setDate(d.getDate() + n); return c; }
function fmtDay(d) { return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }

export default function DispatchBoard() {
  const [view, setView] = useState("week"); // week | day
  const [anchor, setAnchor] = useState(() => mondayOf(new Date()));
  const [board, setBoard] = useState({ engineers: [], assignments: [], unassigned_jobs: [] });
  const [loading, setLoading] = useState(true);
  const [customDlg, setCustomDlg] = useState(null); // {payload, resolve, reject}
  const [conflictDlg, setConflictDlg] = useState(null); // {conflicts, onContinue, onCancel}

  const dates = useMemo(() => {
    if (view === "day") return [anchor];
    return [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(anchor, i));
  }, [anchor, view]);

  const rangeFrom = iso(dates[0]);
  const rangeTo = iso(dates[dates.length - 1]);
  const todayIso = isoLocal(new Date());
  const highlightToday = view === "week"
    ? dates.some((d) => iso(d) === todayIso)
    : iso(anchor) === todayIso;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dispatch/board", { params: { date_from: rangeFrom, date_to: rangeTo } });
      setBoard(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [rangeFrom, rangeTo]);

  useEffect(() => { load(); }, [load]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const bucketed = useMemo(() => {
    const map = new Map();
    for (const a of board.assignments || []) {
      const k = `${a.engineer_id}|${a.date}|${a.slot_type}`;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(a);
    }
    for (const arr of map.values()) arr.sort((x, y) => x.sort_order - y.sort_order);
    return map;
  }, [board]);

  const askCustom = () => new Promise((resolve) => {
    setCustomDlg({ start_time: "09:00", end_time: "12:00", onSubmit: (times) => { setCustomDlg(null); resolve(times); }, onCancel: () => { setCustomDlg(null); resolve(null); } });
  });
  const askConflict = (conflicts) => new Promise((resolve) => {
    setConflictDlg({ conflicts, onContinue: () => { setConflictDlg(null); resolve(true); }, onCancel: () => { setConflictDlg(null); resolve(false); } });
  });

  const performMove = async ({ jobId, slotIndex, engineerId, date, slotType, startTime, endTime, sortOrder }) => {
    // pre-flight conflicts
    try {
      const { data: cdata } = await api.get("/dispatch/conflicts", {
        params: { engineer_id: engineerId, date, slot_type: slotType, start_time: startTime || undefined, end_time: endTime || undefined, job_id: jobId, slot_index: slotIndex },
      });
      const conflicts = cdata.conflicts || [];
      const hard = conflicts.filter((c) => c.severity === "hard");
      const soft = conflicts.filter((c) => c.severity === "soft");
      if (hard.length) {
        toast.error(`Hard conflict with ${hard.map((c) => c.job_number).join(", ")}`);
        return false;
      }
      if (soft.length) {
        const ok = await askConflict(soft);
        if (!ok) return false;
      }
    } catch { /* fall through — /move will hard-block anyway */ }

    try {
      await api.post("/dispatch/move", {
        job_id: jobId,
        slot_index: slotIndex,
        target: {
          engineer_id: engineerId, date, slot_type: slotType,
          start_time: startTime || null, end_time: endTime || null,
          sort_order: sortOrder ?? 0,
        },
      });
      await load();
      toast.success("Job scheduled");
      return true;
    } catch (err) { toast.error(formatApiError(err)); await load(); return false; }
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over) return;
    const src = String(active.id);
    const dst = String(over.id);

    // Draggable ids: "unassigned:<jobId>" | "card:<jobId>:<slotIndex>"
    // Droppable ids: "cell:<engineerId>:<date>:<slotType>" | "unassigned"

    if (!dst.startsWith("cell:") && dst !== "unassigned") return;

    if (dst === "unassigned") {
      if (!src.startsWith("card:")) return;
      const [, jobId, slotIdxStr] = src.split(":");
      try {
        await api.post("/dispatch/remove-from-board", { job_id: jobId, slot_index: Number(slotIdxStr) });
        await load();
        toast.success("Returned to unassigned");
      } catch (err) { toast.error(formatApiError(err)); }
      return;
    }

    const [, engineerId, date, slotType] = dst.split(":");
    const currentCount = (bucketed.get(`${engineerId}|${date}|${slotType}`) || []).length;
    let startTime = null;
    let endTime = null;
    if (slotType === "custom") {
      const t = await askCustom();
      if (!t) return;
      startTime = t.start_time; endTime = t.end_time;
    }

    if (src.startsWith("unassigned:")) {
      const jobId = src.split(":")[1];
      await performMove({ jobId, slotIndex: -1, engineerId, date, slotType, startTime, endTime, sortOrder: currentCount });
    } else if (src.startsWith("card:")) {
      const [, jobId, slotIdxStr] = src.split(":");
      await performMove({ jobId, slotIndex: Number(slotIdxStr), engineerId, date, slotType, startTime, endTime, sortOrder: currentCount });
    }
  };

  const goPrev = () => setAnchor((a) => addDays(a, view === "week" ? -7 : -1));
  const goNext = () => setAnchor((a) => addDays(a, view === "week" ? 7 : 1));
  const goToday = () => setAnchor(view === "week" ? mondayOf(new Date()) : (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })());

  return (
    <div className="p-6 space-y-4" data-testid="dispatch-page">
      <PageHeader
        testId="dispatch-header"
        title="Dispatch Board"
        subtitle={`${rangeFrom} → ${rangeTo}`}
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 border border-border rounded-sm p-0.5">
              <button type="button" onClick={() => setView("week")} className={`px-2 py-1 text-xs rounded-sm ${view === "week" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="dispatch-view-week">Week</button>
              <button type="button" onClick={() => setView("day")} className={`px-2 py-1 text-xs rounded-sm ${view === "day" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="dispatch-view-day">Day</button>
            </div>
            <Button variant="outline" size="sm" onClick={goPrev} data-testid="dispatch-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={goToday} data-testid="dispatch-today">Today</Button>
            <Button variant="outline" size="sm" onClick={goNext} data-testid="dispatch-next"><ChevronRight className="h-4 w-4" /></Button>
            <Input type="date" value={iso(anchor)} onChange={(e) => { const parsed = parseIsoLocal(e.target.value); if (!parsed) return; setAnchor(view === "week" ? mondayOf(parsed) : parsed); }} className="h-8 w-40 text-xs" data-testid="dispatch-date-picker" />
            <Button variant="outline" size="sm" onClick={load} data-testid="dispatch-refresh"><RefreshCw className="h-4 w-4" /></Button>
          </div>
        }
      />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <div className="grid grid-cols-[260px_1fr] gap-4">
          {/* Unassigned rail */}
          <UnassignedRail jobs={board.unassigned_jobs || []} loading={loading} />

          {/* Board grid */}
          <div className="overflow-auto border border-border rounded-md bg-card">
            <table className="w-full text-xs border-collapse" data-testid="dispatch-grid">
              <thead className="bg-muted/50 sticky top-0 z-10">
                <tr>
                  <th className="text-left py-2 px-3 border-b border-border w-40">Engineer</th>
                  {dates.map((d) => {
                    const isToday = highlightToday && iso(d) === todayIso;
                    return (
                      <th
                        key={iso(d)}
                        className={`text-left py-2 px-3 border-b border-l border-border font-bold uppercase tracking-wider ${isToday ? "bg-primary/[0.06] text-primary ring-1 ring-inset ring-primary/30" : ""}`}
                        data-testid={`dispatch-column-header-${iso(d)}`}
                        data-today={isToday ? "true" : "false"}
                      >
                        {fmtDay(d)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {(board.engineers || []).length === 0 ? (
                  <tr><td colSpan={dates.length + 1} className="py-8 text-center text-muted-foreground">No active engineers.</td></tr>
                ) : (board.engineers || []).map((eng) => (
                  <tr key={eng.id} data-testid={`dispatch-row-${eng.id}`}>
                    <th className="text-left py-2 px-3 border-b border-border align-top w-40">
                      <div className="font-medium">{eng.full_name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{eng.username}</div>
                    </th>
                    {dates.map((d) => {
                      const isToday = highlightToday && iso(d) === todayIso;
                      return (
                        <td
                          key={iso(d)}
                          className={`border-b border-l border-border align-top p-1.5 min-w-[170px] ${isToday ? "bg-primary/[0.03]" : ""}`}
                        >
                          {SLOT_TYPES.map((st) => {
                            const bucket = bucketed.get(`${eng.id}|${iso(d)}|${st}`) || [];
                            return (
                              <DropCell key={st} engineerId={eng.id} date={iso(d)} slotType={st} items={bucket} />
                            );
                          })}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DndContext>

      {customDlg && <CustomTimeDialog {...customDlg} />}
      {conflictDlg && <ConflictDialog {...conflictDlg} />}
    </div>
  );
}

function UnassignedRail({ jobs, loading }) {
  const { setNodeRef, isOver } = useDroppable({ id: "unassigned" });
  return (
    <div
      ref={setNodeRef}
      data-testid="dispatch-unassigned-rail"
      className={`border border-border rounded-md bg-card p-3 min-h-[400px] ${isOver ? "ring-2 ring-primary" : ""}`}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Unassigned Jobs · {jobs.length}</div>
      {loading ? <div className="text-xs text-muted-foreground">Loading…</div> : jobs.length === 0 ? (
        <div className="text-xs text-muted-foreground italic" data-testid="dispatch-unassigned-empty">All caught up 🎯</div>
      ) : (
        <ul className="space-y-2">
          {jobs.map((j) => (<li key={j.id}><UnassignedCard job={j} /></li>))}
        </ul>
      )}
    </div>
  );
}

function UnassignedCard({ job }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `unassigned:${job.id}` });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      data-testid={`dispatch-unassigned-${job.id}`}
      className="cursor-grab active:cursor-grabbing border border-border rounded-sm p-2 bg-background hover:bg-muted/30 transition-colors"
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono text-[10px] font-bold">{job.job_number}</span>
        <JobPriorityBadge priority={job.priority} />
      </div>
      <div className="text-xs font-medium mt-0.5 line-clamp-1">{job.title}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{job.customer_name} · {job.site_name}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{job.machine_summary}</div>
    </div>
  );
}

function DropCell({ engineerId, date, slotType, items }) {
  const id = `cell:${engineerId}:${date}:${slotType}`;
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      data-testid={`dispatch-cell-${engineerId}-${date}-${slotType}`}
      className={`border border-dashed rounded-sm mb-1 p-1 min-h-[38px] ${isOver ? "border-primary bg-primary/5" : "border-border/60 bg-background"}`}
    >
      <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground px-0.5">{SLOT_LABEL[slotType]}</div>
      <div className="space-y-1">
        {items.map((a) => (<AssignmentCard key={`${a.job_id}-${a.slot_index}`} a={a} />))}
      </div>
    </div>
  );
}

function AssignmentCard({ a }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `card:${a.job_id}:${a.slot_index}` });
  const multi = a.slot_count > 1;
  const isDone = a.status === "completed" || a.status === "closed";
  const slotTimeLabel = a.slot_type === "custom" && a.start_time
    ? `Custom · ${a.start_time}–${a.end_time || "?"}`
    : SLOT_LABEL[a.slot_type] || a.slot_type;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          ref={setNodeRef}
          {...attributes}
          {...listeners}
          style={{ opacity: isDragging ? 0.5 : isDone ? 0.7 : 1 }}
          data-testid={`dispatch-card-${a.job_id}-${a.slot_index}`}
          data-status={a.status}
          className={`cursor-grab active:cursor-grabbing bg-card border border-border ${STATUS_BORDER[a.status] || ""} border-l-4 rounded-sm p-1.5 hover:shadow-sm transition-shadow`}
        >
          <div className="flex items-center justify-between gap-1">
            <span className="font-mono text-[10px] font-bold">{a.job_number}</span>
            <div className="flex items-center gap-1">
              {multi && <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground border border-border rounded px-1">{a.slot_index + 1}/{a.slot_count}</span>}
              {a.actions_required && <AlertTriangle className="h-3 w-3 text-amber-600" />}
              <a href={`/jobs/${a.job_id}?back=%2Fdispatch`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-muted-foreground hover:text-foreground" data-testid={`dispatch-card-open-${a.job_id}-${a.slot_index}`}><ExternalLink className="h-3 w-3" /></a>
            </div>
          </div>
          <div className="text-[11px] font-medium leading-tight mt-0.5 line-clamp-1">{a.title}</div>
          <div className="text-[10px] text-muted-foreground line-clamp-1">{a.customer_name} · {a.site_name}</div>
          <div className="text-[10px] text-muted-foreground line-clamp-1">{a.machine_summary}</div>
          {a.slot_type === "custom" && a.start_time && (
            <div className="text-[10px] text-muted-foreground mt-0.5">{a.start_time}–{a.end_time}</div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={8}
        className="max-w-sm text-xs bg-popover text-popover-foreground border border-border shadow-md p-3 space-y-2"
        data-testid={`dispatch-card-tooltip-${a.job_id}-${a.slot_index}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[11px] font-bold">{a.job_number}</span>
          <div className="flex items-center gap-1.5">
            <JobStatusBadge status={a.status} />
            <JobPriorityBadge priority={a.priority} />
          </div>
        </div>
        <div className="text-sm font-semibold leading-snug">{a.title || "Untitled job"}</div>
        <div className="space-y-0.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Customer / Site</div>
          <div>{a.customer_name || "—"} · {a.site_name || "—"}</div>
        </div>
        <div className="space-y-0.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Machine</div>
          <div>{a.machine_summary || "—"}</div>
        </div>
        <div className="space-y-0.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Slot</div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span>{a.date} · {slotTimeLabel}</span>
            {multi && (
              <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground border border-border rounded px-1">
                {a.slot_index + 1}/{a.slot_count}
              </span>
            )}
            {a.actions_required && (
              <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 border border-amber-500/40 rounded px-1">Actions</span>
            )}
          </div>
          {a.notes && <div className="text-muted-foreground italic">Slot note: {a.notes}</div>}
        </div>
        {a.description && (
          <div className="space-y-0.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Description</div>
            <div className="whitespace-pre-wrap line-clamp-4">{a.description}</div>
          </div>
        )}
        {a.site_access_notes_snapshot && (
          <div className="space-y-0.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Site access</div>
            <div className="whitespace-pre-wrap line-clamp-4">{a.site_access_notes_snapshot}</div>
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function CustomTimeDialog({ start_time, end_time, onSubmit, onCancel }) {
  const [start, setStart] = useState(start_time);
  const [end, setEnd] = useState(end_time);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-lg rounded-md" data-testid="dispatch-custom-dialog">
        <DialogHeader>
          <DialogTitle>Custom time slot</DialogTitle>
          <DialogDescription>Set the start and end time for this drop.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Start</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="dispatch-custom-start" /></div>
          <div className="space-y-1.5"><Label>End</Label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="dispatch-custom-end" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="dispatch-custom-cancel">Cancel</Button>
          <Button onClick={() => start < end ? onSubmit({ start_time: start, end_time: end }) : toast.error("Start must be before end")} data-testid="dispatch-custom-submit">Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConflictDialog({ conflicts, onContinue, onCancel }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-lg rounded-md" data-testid="dispatch-conflict-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Scheduling overlap</DialogTitle>
          <DialogDescription>The engineer already has overlapping work. Continue anyway?</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-sm">
          {conflicts.map((c, i) => (
            <li key={i} className="border border-border rounded-sm p-2 bg-muted/30" data-testid={`dispatch-conflict-item-${i}`}>
              <span className="font-mono text-xs font-bold">{c.job_number}</span> — {c.slot_type}{c.start_time ? ` ${c.start_time}–${c.end_time}` : ""} on {c.date}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="dispatch-conflict-cancel">Cancel</Button>
          <Button onClick={onContinue} data-testid="dispatch-conflict-continue">Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
